import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "react-hot-toast";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { getPostLoginPath, canAccessPath } from "./config/adminRoles";
import DashboardLayout from "./components/layout/DashboardLayout";
import Dashboard from "./pages/Dashboard";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import PrivateRoute from "./components/PrivateRoute";
import NotFound from "./pages/NotFound";
import SystemUsers from "./pages/SystemUsers";
import AppUsers from "./pages/AppUsers";
import Users from "./pages/Users";
import UserDetail from "./pages/UserDetail";
import Plans from "./pages/Plans";
import Companies from "./pages/Companies";
import CompanyDetail from "./pages/CompanyDetail";
import Organizations from "./pages/Organizations";
import OrganizationDetail from "./pages/OrganizationDetail";
import SupportTickets from "./pages/SupportTickets";
import Blogs from "./pages/Blogs";
import BlogsDetails from "./pages/BlogsDetails";
import BlogCategories from "./pages/BlogCategories";
import HaystackersBlogs from "./pages/HaystackersBlogs";
import HaystackersBlogsDetails from "./pages/HaystackersBlogsDetails";
import HaystackersBlogCategories from "./pages/HaystackersBlogCategories";
import Coupons from "./pages/Coupons";
import Migrations from "./pages/Migrations";

const queryClient = new QueryClient();

function DashboardGate() {
  const { user } = useAuth();
  if (!canAccessPath(user?.role, "/dashboard")) {
    return <Navigate to={getPostLoginPath(user?.role)} replace />;
  }
  return <Dashboard />;
}

function RootHomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={getPostLoginPath(user?.role)} replace />;
}

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/" element={<PrivateRoute />}>
              <Route element={<DashboardLayout />}>
                <Route path="/dashboard" element={<DashboardGate />} />
                <Route path="/dashboard/users" element={<Users />} />
                <Route path="/dashboard/users/:userId" element={<UserDetail />} />
                <Route
                  path="/dashboard/system-users"
                  element={<SystemUsers />}
                />
                <Route path="/dashboard/app-users" element={<AppUsers />} />
                <Route path="/dashboard/plans" element={<Plans />} />
                <Route path="/dashboard/companies" element={<Companies />} />
                <Route path="/dashboard/companies/:companyId" element={<CompanyDetail />} />
                <Route path="/dashboard/organizations" element={<Organizations />} />
                <Route path="/dashboard/organizations/:orgId" element={<OrganizationDetail />} />
                <Route
                  path="/dashboard/support-tickets"
                  element={<SupportTickets />}
                />
                <Route path="/dashboard/blogs" element={<Blogs />} />
                <Route
                  path="/dashboard/blogs-details/:id"
                  element={<BlogsDetails />}
                />
                <Route
                  path="/dashboard/blog-categories"
                  element={<BlogCategories />}
                />
                <Route
                  path="/dashboard/haystackers-blogs"
                  element={<HaystackersBlogs />}
                />
                <Route
                  path="/dashboard/haystackers-blogs-details/:id"
                  element={<HaystackersBlogsDetails />}
                />
                <Route
                  path="/dashboard/haystackers-blog-categories"
                  element={<HaystackersBlogCategories />}
                />
                <Route path="/dashboard/coupons" element={<Coupons />} />
                <Route path="/dashboard/migrations" element={<Migrations />} />
                <Route path="/" element={<RootHomeRedirect />} />
                <Route path="/*" element={<NotFound />} />
              </Route>
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
          <Toaster position="top-center" />
        </AuthProvider>
      </Router>
    </QueryClientProvider>
  );
};

export default App;
